import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { axiosClient } from '../lib/api.ts';
import { useStore } from '../lib/store.ts';

/** Devices a rider can pick (docs/ONBOARDING_PLAN.md §6.1). */
export type SetupDevice = 'garmin' | 'zwift' | 'apple_watch' | 'android_watch' | 'bike_computer' | 'phone';

export const SETUP_DEVICES: readonly SetupDevice[] = [
  'garmin',
  'zwift',
  'apple_watch',
  'android_watch',
  'bike_computer',
  'phone',
];

/** Ways to get rides in, in the order the server recommends them (§6.2). */
export type SetupPathId =
  | 'garmin_connect'
  | 'health_via_garmin'
  | 'intervals'
  | 'health_via_zwift'
  | 'health_via_app'
  | 'health'
  | 'upload';

export type SetupPathStatus = 'connected' | 'not_connected' | 'waiting';

export interface SetupPath {
  id: SetupPathId;
  for: SetupDevice[];
  status: SetupPathStatus;
  rides: number;
  last_ride_at: string | null;
}

export type ChecklistItemId = 'connect' | 'history' | 'ftp' | 'notifications';

export interface ChecklistItem {
  id: ChecklistItemId;
  done: boolean;
}

/** ``GET /setup`` (§6.1). */
export interface SetupStatus {
  onboarding_required: boolean;
  devices: SetupDevice[];
  has_rides: boolean;
  rides: number;
  last_ride_at: string | null;
  stale: { days: number; source: string } | null;
  paths: SetupPath[];
  history: { zip_import: 'pro' | 'available'; imported_rides: number };
  checklist: { visible: boolean; items: ChecklistItem[] };
}

/** ``PUT /setup`` body, any subset (§6.3). */
export interface SetupUpdate {
  devices?: SetupDevice[];
  completed?: boolean;
  checklist_dismissed?: boolean;
  history_skipped?: boolean;
}

export const SETUP_KEY = ['setup'] as const;

/** How often the Connect step re-reads ``/setup`` for live status. */
export const SETUP_POLL_MS = 5_000;

/**
 * Setup state for onboarding, the checklist, empty states and the stale
 * banner. ``poll`` refetches every 5 s (the wizard's Connect step).
 */
export function useSetup({ poll = false }: { poll?: boolean } = {}) {
  return useQuery<SetupStatus>({
    queryKey: SETUP_KEY,
    queryFn: () => axiosClient.get<SetupStatus>('/setup').then((r) => r.data),
    staleTime: 30_000,
    refetchInterval: poll ? SETUP_POLL_MS : false,
  });
}

/** ``PUT /setup``; the response is the new ``GET /setup`` body. */
export function useUpdateSetup() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const addToast = useStore((s) => s.addToast);
  return useMutation({
    mutationFn: (body: SetupUpdate) => axiosClient.put<SetupStatus>('/setup', body).then((r) => r.data),
    onSuccess: (data) => queryClient.setQueryData(SETUP_KEY, data),
    onError: () => addToast({ message: t('setup.saveFailed'), type: 'error' }),
  });
}

/** True only when ``/setup`` loaded and says there are no rides (never while loading). */
export function useNoRides(): boolean {
  const { data } = useSetup();
  return data?.has_rides === false;
}
